import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, AlertCircle, Activity, Database, Shield, TestTube } from "lucide-react";
import { Button } from "@/components/ui/button";

export const AdminSystemHealth = () => {
  const healthChecks = [
    {
      name: "Database Connection",
      status: "healthy",
      icon: Database,
      lastCheck: new Date().toLocaleString(),
    },
    {
      name: "Authentication Service",
      status: "healthy",
      icon: Shield,
      lastCheck: new Date().toLocaleString(),
    },
    {
      name: "Storage Service",
      status: "healthy",
      icon: Activity,
      lastCheck: new Date().toLocaleString(),
    },
  ];

  const testStats = {
    unit: {
      total: 8,
      passed: 8,
      failed: 0,
      coverage: "78%",
    },
    e2e: {
      total: 3,
      passed: 3,
      failed: 0,
      lastRun: new Date().toLocaleString(),
    },
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "healthy":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "warning":
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case "error":
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Activity className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "destructive" | "secondary"> = {
      healthy: "default",
      warning: "secondary",
      error: "destructive",
    };
    return (
      <Badge variant={variants[status] || "secondary"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            System Health Status
          </CardTitle>
          <CardDescription>
            Monitor the health of backend services and infrastructure
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {healthChecks.map((check) => (
              <div
                key={check.name}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <check.icon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{check.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Last checked: {check.lastCheck}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusIcon(check.status)}
                  {getStatusBadge(check.status)}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="h-5 w-5" />
            Test Coverage & Results
          </CardTitle>
          <CardDescription>
            Automated testing status and code coverage metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Unit Tests</h3>
                <Badge variant="default">
                  {testStats.unit.passed}/{testStats.unit.total} Passed
                </Badge>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Tests</span>
                  <span className="font-medium">{testStats.unit.total}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Passed</span>
                  <span className="font-medium text-green-600">{testStats.unit.passed}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Failed</span>
                  <span className="font-medium text-red-600">{testStats.unit.failed}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Code Coverage</span>
                  <span className="font-medium">{testStats.unit.coverage}</span>
                </div>
              </div>
              <Button variant="outline" size="sm" className="w-full">
                Run Unit Tests
              </Button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">E2E Tests</h3>
                <Badge variant="default">
                  {testStats.e2e.passed}/{testStats.e2e.total} Passed
                </Badge>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Tests</span>
                  <span className="font-medium">{testStats.e2e.total}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Passed</span>
                  <span className="font-medium text-green-600">{testStats.e2e.passed}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Failed</span>
                  <span className="font-medium text-red-600">{testStats.e2e.failed}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Last Run</span>
                  <span className="font-medium">{testStats.e2e.lastRun}</span>
                </div>
              </div>
              <Button variant="outline" size="sm" className="w-full">
                Run E2E Tests
              </Button>
            </div>
          </div>

          <div className="mt-6 p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Test Commands:</strong>
              <br />
              Unit Tests: <code className="text-xs bg-background px-2 py-1 rounded">npm run test</code>
              <br />
              E2E Tests: <code className="text-xs bg-background px-2 py-1 rounded">npm run test:e2e</code>
              <br />
              Coverage: <code className="text-xs bg-background px-2 py-1 rounded">npm run test:coverage</code>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
